import { useEffect, useMemo, useState } from 'react'

import { useUser } from 'contexts/user.context'

import * as S from './style'

type CollaborationUser = {
  id: string
  firstname: string
  lastname: string
  profilePicture?: string
}

type Props = {
  selectedIds: string[]
  onChange: (ids: string[]) => void
  excludeIds?: string[]
  disabled?: boolean
}

const CollaborationPicker = ({
  selectedIds = [],
  onChange,
  excludeIds = [],
  disabled = false
}: Props) => {
  const { listUsers, user: currentUser } = useUser()
  const [users, setUsers] = useState<CollaborationUser[]>([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    ;(async () => {
      const response = await listUsers()
      setUsers(response)
    })()
  }, [listUsers])

  const availableUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    const excluded = new Set(
      [currentUser?.id, ...selectedIds, ...excludeIds].filter(Boolean)
    )

    return users
      .filter((user) => !excluded.has(user.id))
      .filter((user) => {
        if (!term) return false
        const fullName = `${user.firstname} ${user.lastname}`.toLocaleLowerCase(
          'pt-BR'
        )
        return fullName.includes(term)
      })
      .slice(0, 6)
  }, [currentUser?.id, excludeIds, search, selectedIds, users])

  const selectedUsers = useMemo(
    () => users.filter((user) => selectedIds.includes(user.id)),
    [selectedIds, users]
  )

  const addUser = (userId: string) => {
    if (disabled) return
    onChange(Array.from(new Set([...selectedIds, userId])))
    setSearch('')
  }

  const removeUser = (userId: string) => {
    if (disabled) return
    onChange(selectedIds.filter((id) => id !== userId))
  }

  return (
    <S.Wrapper>
      <S.Intro>
        <strong>Colaboração</strong>
        <span>
          Viajaram juntos? Convide outros viajantes da RedSterna para participar
          deste roteiro.
        </span>
      </S.Intro>

      {!disabled && (
        <S.SearchInput
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar viajante pelo nome..."
        />
      )}

      {availableUsers.length > 0 && (
        <S.Results>
          {availableUsers.map((user) => (
            <S.ResultButton
              key={user.id}
              type="button"
              onClick={() => addUser(user.id)}
            >
              <S.Avatar
                src={user.profilePicture || '/images/profile-empty.png'}
                alt=""
              />
              <span>
                {user.firstname} {user.lastname}
              </span>
              <b>Convidar</b>
            </S.ResultButton>
          ))}
        </S.Results>
      )}

      {selectedUsers.length > 0 && (
        <S.SelectedList>
          {selectedUsers.map((user) => (
            <S.SelectedItem key={user.id}>
              <S.Avatar
                src={user.profilePicture || '/images/profile-empty.png'}
                alt=""
              />
              <span>
                {user.firstname} {user.lastname}
              </span>
              <small>Será convidado após salvar</small>
              {!disabled && (
                <button type="button" onClick={() => removeUser(user.id)}>
                  ×
                </button>
              )}
            </S.SelectedItem>
          ))}
        </S.SelectedList>
      )}
    </S.Wrapper>
  )
}

export default CollaborationPicker
